/**
 * AssignedPlansSection — plans the reader's church, province or event has
 * given them. Anything whose start date has passed is already under way; the
 * rest are shown with the date they begin.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { BookMarked, Church, Globe2, CalendarDays, Play, Clock } from 'lucide-react';

const ICONS = { church: Church, province: Globe2, event: CalendarDays };

export const AssignedPlansSection = ({ onViewPlan }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['my-assigned-bible-plans'],
    queryFn: () => api.get('/bible/my-assigned-plans').then((r) => r.data),
  });

  if (isLoading) return null;
  const plans = data?.plans || [];
  if (plans.length === 0) return null;

  return (
    <div data-testid="assigned-plans-section">
      <h2 className="text-xl font-semibold text-slate-900 mb-1">Assigned to you</h2>
      <p className="text-sm text-slate-500 mb-4">
        Reading set by your church, your province or an event you are registered for.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {plans.map((p) => {
          const a = p.assignment || {};
          const Icon = ICONS[a.target_type] || Church;
          const prog = p.progress;
          return (
            <Card key={`${p.id}-${a.id}`} className="overflow-hidden"
                  data-testid={`assigned-plan-card-${p.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                    <BookMarked className="h-4 w-4 text-amber-700" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-sm truncate">{p.title}</h3>
                    <p className="text-xs text-slate-500">
                      {p.duration_days} days · {p.category || 'general'}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant="outline" className="text-[10px] gap-1">
                    <Icon className="h-3 w-3" /> {a.target_name}
                  </Badge>
                  {a.started ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
                      Started {a.start_date}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] gap-1">
                      <Clock className="h-3 w-3" /> Starts {a.start_date}
                    </Badge>
                  )}
                </div>

                {prog ? (
                  <>
                    <Progress value={prog.completion_percent} className="h-1.5" />
                    <Button size="sm" variant="outline" className="w-full"
                            onClick={() => onViewPlan?.(prog.user_plan_id)}
                            data-testid={`assigned-plan-continue-${p.id}`}>
                      <Play className="h-3.5 w-3.5 mr-1" /> Continue · Day {prog.current_day}
                    </Button>
                  </>
                ) : (
                  <p className="text-xs text-slate-500" data-testid={`assigned-plan-waiting-${p.id}`}>
                    This one opens itself on {a.start_date}.
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default AssignedPlansSection;
